import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IZone, IZoneInput, zonesAtom, zonesLoadingAtom } from "./types";

type UseZones = [IZone[], {
    list: (query?: Record<string, any>) => Promise<IZone[] | null>;
    getById: (id: number) => Promise<IZone | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IZone[] | null>;
    create: (input: IZoneInput) => Promise<IZone | null>;
    update: (id: number, input: IZoneInput) => Promise<IZone | null>;
    remove: (id: number) => Promise<IZone | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useZones = (): UseZones => {
    const [items, setItems] = useAtom(zonesAtom);
    const setLoading = useSetAtom(zonesLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getZones(query: $query){ id zone_group_id code name is_active width height length x y z created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getZones);
            return data.getZones as IZone[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch zones.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getZoneById(id:$id){ id zone_group_id code name is_active width height length x y z created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getZoneById as IZone;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedZones(first:$first,offset:$offset,query:$query){ id zone_group_id code name is_active width height length x y z created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedZones as IZone[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IZoneInput) => {
        try { const q = `mutation($input:ZoneInput!){ createZone(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createZone as IZone;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IZoneInput) => {
        try { const q = `mutation($id:ID!,$input:ZoneInput!){ updateZone(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateZone as IZone;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteZone(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteZone as IZone;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


